import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  Paper,
  BottomDrawer,
  ConfirmDialog,
  DetailField,
  EntityDetailShell,
  formatAddress,
} from '@vipso/shared';
import {
  useGetCustomerQuery,
  useAddUpdateCustomerMutation,
  useDeleteCustomerMutation,
} from '../api/tenantApi';
import {
  CustomerForm,
  EMPTY_CUSTOMER_FORM,
  customerFormInput,
  customerFormValues,
  type CustomerFormValues,
} from '../features/customers/CustomerForm';
import { ApprovalStatusBadge } from '../components/ApprovalStatusBadge';
import { EntityApprovalPanel } from '../components/EntityApprovalPanel';

export function CustomerDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetCustomerQuery(id!, { skip: !id });
  const [addUpdateCustomer, { isLoading: saving }] = useAddUpdateCustomerMutation();
  const [deleteCustomer, { isLoading: deleting }] = useDeleteCustomerMutation();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState<CustomerFormValues>(EMPTY_CUSTOMER_FORM);

  const customer = data?.getCustomer;

  function openEdit() {
    if (!customer) return;
    setForm(customerFormValues(customer));
    setEditOpen(true);
  }

  async function handleSave() {
    if (!customer) return;
    await addUpdateCustomer(customerFormInput(form, customer)).unwrap();
    setEditOpen(false);
  }

  async function handleDelete() {
    if (!customer) return;
    await deleteCustomer(customer.id).unwrap();
    setConfirmOpen(false);
    navigate('/customers');
  }

  return (
    <EntityDetailShell
      isLoading={isLoading}
      notFound={isError || !customer}
      notFoundTitle={t('customers.notFound')}
      backLabel={t('customers.backToList')}
      onBack={() => navigate('/customers')}
      title={customer?.name ?? ''}
      description={t('customers.details')}
      actions={
        <Button variant="light" onClick={openEdit}>
          {t('common.edit')}
        </Button>
      }
    >
      <Paper p="lg" withBorder radius="md">
        <Stack gap="md">
          <DetailField label={t('common.phone')} value={customer?.phone} />
          <DetailField label={t('common.email')} value={customer?.email} />
          <DetailField
            label={t('approval.status')}
            value={<ApprovalStatusBadge status={customer?.approvalStatus} />}
          />
          <DetailField label={t('customers.openingBalance')} value={customer?.openingBalance ?? 0} />
          <DetailField label={t('common.address')} value={formatAddress(customer?.address)} />
        </Stack>
      </Paper>

      <EntityApprovalPanel
        subjectType="CUSTOMER"
        subjectId={customer?.id}
        entityApprovalStatus={customer?.approvalStatus}
      />

      <Button
        onClick={() => navigate(`/orders?customerIds=${customer?.id}`)}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('common.transactions')}
      </Button>

      <BottomDrawer opened={editOpen} onClose={() => setEditOpen(false)} title={t('customers.edit')}>
        <Stack gap="md">
          <CustomerForm value={form} onChange={setForm} />
          <Group justify="space-between">
            <Button color="red" variant="light" onClick={() => setConfirmOpen(true)}>
              {t('common.delete')}
            </Button>
            <Group>
              <Button variant="default" onClick={() => setEditOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button loading={saving} onClick={() => void handleSave()} disabled={!form.name.trim()}>
                {t('common.save')}
              </Button>
            </Group>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('customers.deleteConfirm')}
        message={t('common.deleteMessage', { name: customer?.name ?? '' })}
        confirmLabel={t('common.delete')}
        loading={deleting}
        danger
      />
    </EntityDetailShell>
  );
}
